import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCancelBooking, useMyBookings } from '../queries';
import { addDays, describe, today } from '../dates';
import { SLOT_LABEL, type MyBooking } from '../types';

function groupByDate(bookings: MyBooking[]): [string, MyBooking[]][] {
  const groups = new Map<string, MyBooking[]>();
  for (const booking of bookings) {
    groups.set(booking.date, [...(groups.get(booking.date) ?? []), booking]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function MyBookingsPage() {
  const from = today();
  const to = addDays(from, 60);
  const bookings = useMyBookings(from, to);
  const cancel = useCancelBooking();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await cancel.mutateAsync(id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not cancel that.');
    } finally {
      setBusy(null);
    }
  };

  if (bookings.isPending) {
    return <div className="page"><p className="muted">Loading…</p></div>;
  }

  const groups = groupByDate(bookings.data ?? []);

  if (groups.length === 0) {
    return (
      <div className="page">
        <div className="card center">
          <h2>Nothing booked yet</h2>
          <p className="muted">Pick a day and grab a desk or a bay.</p>
          <p><Link to="/book"><button className="primary">Book a desk</button></Link></p>
        </div>
      </div>
    );
  }

  return (
    <div className="page stack">
      <h1>Your bookings</h1>
      {error && <p className="notice">{error}</p>}

      {groups.map(([date, items]) => (
        <div key={date} className="card">
          <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--rule)' }}>
            <h3>{describe(date)}</h3>
          </div>
          <div className="list">
            {items.map((booking) => (
              <div key={booking.id} className="list-row" style={{ cursor: 'default' }}>
                <span className="label">{booking.label}</span>
                <span className="meta">
                  {booking.kind === 'parking' ? 'Parking' : booking.area_name}
                  {' · '}
                  {SLOT_LABEL[booking.slot]}
                </span>
                <button
                  className="danger"
                  onClick={() => remove(booking.id)}
                  disabled={busy === booking.id}
                >
                  {busy === booking.id ? 'Cancelling…' : 'Cancel'}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      <p className="muted">
        Cancelling frees the desk or bay for someone else immediately.
      </p>
    </div>
  );
}
